import axios from 'axios';
import { sequoiaToken, sequoiaURL } from '../../lib/config';

/**
 * Sequoia's Community Fibre qualification
 * (POST /communityfibre/qualification, see the sequoia API reference).
 */

/** One catalogue tier with Community Fibre's verdict for it. */
export interface CfPlanResult {
  /** cf-<download>-<upload>, Mbps */
  id: string;
  download: number;
  upload: number;
  sku: string;
  /** Green: can be ordered */
  available: boolean;
  /** Yellow: subject to a feasibility check, not sold as available */
  feasibilityCheck: boolean;
  confidence: string;
  installationDays?: number;
}

export interface CfQualification {
  network: 'communityfibre';
  /** The Community Fibre surface that answered. */
  surface?: 'mef' | 'npis';
  serviceable: boolean;
  reason?: 'unit required' | 'address not found' | 'no plan available';
  gnafId?: string;
  /**
   * The building the place is in, from G-NAF: every unit in one building
   * answers the same id. Sent with the order, which is how the CRM keys a
   * Community Fibre building so penetration counts its units together.
   */
  buildingGnafId?: string;
  formattedAddress?: string;
  latitude?: number;
  longitude?: number;
  /** A building-level id, or an address without a unit: ask for the unit and qualify again. */
  unitRequired?: boolean;
  unitCount?: number;
  /** fttp or gfast, from Community Fibre's site record; absent when unknown. */
  accessTechnology?: 'fttp' | 'gfast';
  maxDownload: number;
  maxUpload: number;
  plans: CfPlanResult[];
  qualificationId?: string;
  guaranteedUntil?: string;
  offeredSkus?: string[];
  cached?: boolean;
}

export interface CfAddress {
  unit?: string;
  streetNumber: string;
  street: string;
  suburb: string;
  state: string;
  postcode: string;
}

/**
 * The place: its G-NAF id when the address carries one, and its parts.
 * Sequoia uses the id and falls back to the parts when Community Fibre
 * runs a different G-NAF release and does not know the id.
 */
export interface CfRequest {
  gnafId?: string;
  address: CfAddress;
  /** Which Community Fibre surface sequoia asks: mef (left out) or npis. */
  api?: 'mef' | 'npis';
}

export type CfOutcome =
  | { kind: 'answer'; qualification: CfQualification }
  /** 502/503: Community Fibre could not be asked or is not set up; fall back to DGtek. */
  | { kind: 'unavailable'; status: number }
  | { kind: 'error'; status: number; message: string };

export const qualifyCommunityFibre = async (request: CfRequest): Promise<CfOutcome> => {
  const response = await axios.post<CfQualification | { error?: string }>(
    `${sequoiaURL}/communityfibre/qualification`,
    request,
    {
      headers: { Authorization: `Bearer ${sequoiaToken}`, 'Content-Type': 'application/json' },
      timeout: 15000,
      // Every status is an answer here; the caller decides what to do with it.
      validateStatus: () => true,
    },
  );
  if (response.status === 200) {
    return { kind: 'answer', qualification: response.data as CfQualification };
  }
  if (response.status === 502 || response.status === 503) {
    return { kind: 'unavailable', status: response.status };
  }
  const body = response.data as { error?: string } | undefined;
  return { kind: 'error', status: response.status, message: body?.error ?? `HTTP ${response.status}` };
};
